import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import { CloudOff, MapPin, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { boundsOf, clusterByDistance, PIN_TONE_COLOR, placeLabels } from './mapModel';

/*
  The Mapbox map of the Projects map view (03 #10). Mapbox draws the streets; the pins, cluster bubbles and
  the pin card are React, laid over the map at the positions Mapbox computes (map.project) and moved with it.

  places: [{ key, name, pin, tone, projects, … }] (only places with a pin are drawn); faded: places outside
  the status tab, drawn small and pale; hoveredKey / selectedKey: the row the pointer is on / the open card
  (the selected place always shows as its own pin, never inside a bubble); focus: { key, seq } — a row of
  the list was picked: bring its pin into view, close enough to tell it from its neighbours;
  onHover(key | null), onSelect(key | null); renderCard(place, { close }) → the card's content.
  The card takes the keyboard focus when it opens (screen readers announce it) and Escape closes it, giving
  the focus back to the row or pin that opened it.
*/

const CLUSTER_RADIUS = 34;
const CARD_WIDTH = 268;
// Names next to the pins only while few are on screen (else they would cover each other and the streets);
// placeLabels() then drops any name that would cover another pin, bubble or name.
const MAX_LABELLED_PINS = 14;
// Room kept free around the pins when fitting them: the legend sits bottom-left, the zoom buttons bottom-right.
const FIT_PADDING = { top: 50, bottom: 86, left: 46, right: 56 };
const FOCUS_ZOOM = 11;
// A pin card is ~270 px wide: on a map narrower than this (phones) it cannot sit beside its pin, so it goes
// below or above it — and a row picked in the list brings its pin to the upper part of the map first.
const NARROW_MAP = 600;
const CARD_GAP = 22;
const HALO = '[text-shadow:0_0_3px_var(--surface),0_0_3px_var(--surface),0_0_2px_var(--surface)]';
const isDarkTheme = () => typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark_brand';

function PinMarker({ place, x, y, hovered, selected, faded, labelled, onSelect, onHover }) {
  const color = PIN_TONE_COLOR[place.tone] || PIN_TONE_COLOR.gray;
  const big = hovered || selected;
  const size = faded ? 18 : big ? 34 : 26;
  const count = place.projects.length;
  return (
    <button
      type="button"
      className={cn(
        'pointer-events-auto absolute flex -translate-x-1/2 -translate-y-full items-end justify-center rounded-full transition-[width,height] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        faded && 'opacity-50',
      )}
      style={{ left: x, top: y, width: size, height: size + 4, zIndex: big ? 30 : faded ? 5 : 10 }}
      onClick={() => onSelect(place.key)}
      onMouseEnter={() => onHover(place.key)}
      onMouseLeave={() => onHover(null)}
      onFocus={() => onHover(place.key)}
      onBlur={() => onHover(null)}
      aria-label={`${place.name}${count > 1 ? ` (${count} systems)` : ''}: show details`}
      aria-pressed={selected}
      data-testid={`map-pin-${place.key}`}
      data-place-key={place.key}
      data-tone={place.tone}
      data-faded={faded ? 'true' : undefined}
    >
      {big ? <span className="absolute bottom-[2px] left-1/2 h-[18px] w-[30px] -translate-x-1/2 rounded-[50%] bg-[color:var(--brand-soft)]" aria-hidden="true" /> : null}
      <svg viewBox="0 0 24 28" width={size} height={size + 4} className="relative drop-shadow-[0_1px_2px_rgba(18,18,90,0.35)]" aria-hidden="true">
        <path d="M12 27s9-8.4 9-15.5A9 9 0 0 0 3 11.5C3 18.6 12 27 12 27Z" fill={color} stroke="#fff" strokeWidth="2" />
        {count > 1 && !faded ? (
          <text x="12" y="15" textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff">{count}</text>
        ) : (
          <circle cx="12" cy="11.5" r="3.2" fill="#fff" />
        )}
      </svg>
      {(labelled || big) && !faded ? (
        <span className={cn('pointer-events-none absolute left-full top-1/2 ml-1 -translate-y-1/2 whitespace-nowrap text-[11.5px] font-semibold text-ink', HALO)} aria-hidden="true">
          {place.name}
        </span>
      ) : null}
    </button>
  );
}

/** The name a cluster bubble can carry: its site's, when all its pins are one site (else none). */
const clusterName = (cluster) => {
  const names = [...new Set(cluster.items.map((item) => item.place.name))];
  return names.length === 1 ? names[0] : '';
};

function ClusterBubble({ cluster, hovered, labelled, onClick }) {
  const count = cluster.items.reduce((sum, item) => sum + item.place.projects.length, 0);
  const name = clusterName(cluster);
  const label = name || `${cluster.items.length} sites`;
  return (
    <button
      type="button"
      className={cn(
        'pointer-events-auto absolute flex -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
      style={{ left: cluster.x, top: cluster.y, zIndex: hovered ? 30 : 20 }}
      onClick={() => onClick(cluster)}
      aria-label={`${count} projects at ${label}: zoom in`}
      data-testid={`map-cluster-${cluster.items.length}`}
    >
      <span
        className={cn(
          'grid h-[34px] min-w-[34px] place-items-center rounded-full border-2 border-white bg-navy px-1.5 text-[13px] font-bold tabular-nums text-white shadow-raised',
          hovered && 'ring-4 ring-navy-100',
        )}
      >
        {count}
      </span>
      {labelled && name ? (
        <span className={cn('whitespace-nowrap text-[11.5px] font-semibold text-ink-2', HALO)} aria-hidden="true">
          {name}
        </span>
      ) : null}
    </button>
  );
}

export function MapboxProjectsMap({
  token,
  places,
  faded = [],
  hoveredKey,
  selectedKey,
  onHover,
  onSelect,
  focus,
  renderCard,
  className,
  testId = 'projects-mapbox',
}) {
  const containerRef = useRef(null);
  const overlayRef = useRef(null);
  const cardRef = useRef(null);
  const returnFocusRef = useRef(null);
  const mapRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [, setFrame] = useState(0); // bumped on every map move so the overlay follows
  const [size, setSize] = useState({ width: 0, height: 0 });

  const pinned = useMemo(() => places.filter((place) => place.pin), [places]);
  const pinnedFaded = useMemo(() => faded.filter((place) => place.pin), [faded]);
  const fitKey = pinned.map((place) => place.key).join('|');

  // Create the map once (Mapbox is loaded only when the map is shown).
  useEffect(() => {
    let cancelled = false;
    let map;
    let frame = 0;
    import('mapbox-gl').then((module) => {
      const mapboxgl = module.default || module;
      if (cancelled || !containerRef.current) return;
      mapboxgl.accessToken = token;
      map = new mapboxgl.Map({
        container: containerRef.current,
        style: isDarkTheme() ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11',
        center: [54.9, 24.75], // between Abu Dhabi and Dubai until the pins are fitted
        zoom: 7.4,
        attributionControl: true,
        cooperativeGestures: false,
      });
      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right');
      const bump = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          setFrame((value) => value + 1);
        });
      };
      map.on('move', bump);
      map.on('resize', bump);
      map.on('load', () => {
        if (!cancelled) setReady(true);
      });
      map.on('error', (event) => {
        // Bad / revoked key (401), blocked network: say so over the map; the list beside it keeps working.
        const status = event?.error?.status;
        if (!cancelled && (status === 401 || status === 403 || !mapRef.current?.loaded?.())) {
          setError(status === 401 || status === 403 ? 'Mapbox did not accept the map key.' : 'The map could not load.');
        }
      });
      mapRef.current = map;
    }).catch(() => {
      if (!cancelled) setError('The map could not load.');
    });
    return () => {
      cancelled = true;
      if (frame) window.cancelAnimationFrame(frame);
      mapRef.current = null;
      map?.remove();
    };
  }, [token]);

  // Track the overlay size (card placement) — the map resizes itself with its container.
  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;
    const measure = () => setSize({ width: element.clientWidth, height: element.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      measure();
      mapRef.current?.resize?.();
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Fit the pins whenever the set of places on the map changes (filters, status tab).
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    const bounds = boundsOf([...pinned, ...(pinned.length ? [] : pinnedFaded)].map((place) => place.pin));
    if (!bounds) return;
    const [[west, south], [east, north]] = bounds;
    if (west === east && south === north) map.jumpTo({ center: [west, south], zoom: 13 });
    else map.fitBounds(bounds, { padding: FIT_PADDING, maxZoom: 13, duration: 0 });
  }, [ready, fitKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // A row picked in the list: centre its pin, zoomed in far enough to stand apart from its neighbours.
  useEffect(() => {
    const map = mapRef.current;
    const place = focus && [...pinned, ...pinnedFaded].find((item) => item.key === focus.key);
    if (!ready || !map || !place) return;
    const narrow = (containerRef.current?.clientWidth || 0) < NARROW_MAP;
    const height = containerRef.current?.clientHeight || 0;
    map.easeTo({
      center: [place.pin.lng, place.pin.lat],
      zoom: Math.max(map.getZoom(), FOCUS_ZOOM),
      duration: 450,
      // Narrow map: the pin lands ~70 px from the top, leaving the room below it for its card.
      ...(narrow && height > 200 ? { offset: [0, -Math.round(height / 2 - 70)] } : {}),
    });
  }, [ready, focus?.seq]); // eslint-disable-line react-hooks/exhaustive-deps

  const project = useCallback((pin) => {
    const map = mapRef.current;
    if (!map) return null;
    const point = map.project([pin.lng, pin.lat]);
    return { x: point.x, y: point.y };
  }, []);

  const map = mapRef.current;
  const positioned = ready && map
    ? pinned.map((place) => ({ key: place.key, place, ...project(place.pin) }))
    : [];
  // The open card's place stays a pin of its own (its card points at it), the others may join bubbles.
  const clusters = [
    ...clusterByDistance(positioned.filter((item) => item.key !== selectedKey), CLUSTER_RADIUS),
    ...positioned.filter((item) => item.key === selectedKey).map((item) => ({ key: item.key, x: item.x, y: item.y, items: [item] })),
  ];
  const onScreen = clusters.filter((cluster) => cluster.items.length === 1
    && cluster.x >= 0 && cluster.y >= 0 && cluster.x <= size.width && cluster.y <= size.height).length;
  const labelled = onScreen <= MAX_LABELLED_PINS
    ? placeLabels(clusters.map((cluster) => ({
      key: cluster.key,
      x: cluster.x,
      y: cluster.y,
      kind: cluster.items.length === 1 ? 'pin' : 'cluster',
      text: cluster.items.length === 1 ? cluster.items[0].place.name : clusterName(cluster),
    })), size)
    : new Set();
  const fadedPositioned = ready && map ? pinnedFaded.map((place) => ({ key: place.key, place, ...project(place.pin) })) : [];
  const selected = [...positioned, ...fadedPositioned].find((item) => item.key === selectedKey);

  const zoomToCluster = (cluster) => {
    const bounds = boundsOf(cluster.items.map((item) => item.place.pin));
    const [[west, south], [east, north]] = bounds;
    // Pins on (almost) the same spot never split by zooming: open the first one's card instead.
    if (Math.abs(east - west) < 0.0005 && Math.abs(north - south) < 0.0005) {
      onSelect(cluster.items[0].key);
      return;
    }
    mapRef.current?.fitBounds(bounds, { padding: 90, maxZoom: 16, duration: 500 });
  };

  // The open card takes the focus; closing it gives the focus back to what opened it (or to its pin).
  const cardKey = selected && renderCard ? selected.key : null;
  useEffect(() => {
    if (!cardKey) return;
    const opener = typeof document !== 'undefined' ? document.activeElement : null;
    if (!cardRef.current?.contains(opener)) returnFocusRef.current = opener && opener !== document.body ? opener : null;
    cardRef.current?.focus({ preventScroll: true });
  }, [cardKey]);
  const closeCard = () => {
    const closing = selectedKey;
    const back = returnFocusRef.current;
    returnFocusRef.current = null;
    onSelect(null);
    if (back?.isConnected) {
      back.focus({ preventScroll: true });
      return;
    }
    const pin = [...(overlayRef.current?.querySelectorAll('[data-place-key]') || [])].find((element) => element.dataset.placeKey === closing);
    pin?.focus({ preventScroll: true });
  };
  const onKeyDown = (event) => {
    if (event.key !== 'Escape' || !cardKey) return;
    event.stopPropagation();
    closeCard();
  };

  // The card's real height (it differs per place), for placing it above / below its pin on narrow maps.
  const [cardHeight, setCardHeight] = useState(170);
  useLayoutEffect(() => {
    const height = cardRef.current?.offsetHeight;
    if (height && Math.abs(height - cardHeight) > 1) setCardHeight(height);
  }, [cardKey, cardHeight, size.width]);

  let card = null;
  if (selected && renderCard) {
    const width = Math.min(selected.place.projects.length > 1 ? CARD_WIDTH + 36 : CARD_WIDTH, Math.max(160, size.width - 16));
    const fitsRight = selected.x + CARD_GAP + width <= size.width - 8;
    const fitsLeft = selected.x - CARD_GAP - width >= 8;
    let left;
    let top;
    if (fitsRight || fitsLeft) {
      // Beside the pin (right first), level with it.
      left = fitsRight ? selected.x + CARD_GAP : selected.x - CARD_GAP - width;
      top = Math.min(Math.max(8, selected.y - 70), Math.max(8, size.height - cardHeight - 8));
    } else {
      // Narrow map: below the pin's tip, else above the pin (~34 px tall), else at the bottom — never on it.
      left = Math.min(Math.max(8, selected.x - width / 2), Math.max(8, size.width - width - 8));
      const below = selected.y + 10;
      const above = selected.y - 40 - cardHeight;
      if (below + cardHeight <= size.height - 8) top = below;
      else if (above >= 8) top = above;
      else top = Math.max(8, size.height - cardHeight - 8);
    }
    card = (
      <div
        ref={cardRef}
        tabIndex={-1}
        className="pointer-events-auto absolute z-40 rounded-xl border border-line bg-surface p-3 shadow-overlay outline-none focus-visible:ring-2 focus-visible:ring-ring"
        style={{ left, top, width }}
        role="dialog"
        aria-modal="false"
        aria-label={`${selected.place.name} on the map`}
        data-testid="map-pin-card"
      >
        <button
          type="button"
          onClick={closeCard}
          className="absolute right-1.5 top-1.5 grid h-7 w-7 place-items-center rounded-lg text-ink-3 hover:bg-surface-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Close"
          data-testid="map-pin-card-close"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
        {renderCard(selected.place, { close: closeCard })}
      </div>
    );
  }

  return (
    // Escape anywhere in the map (the card, a pin, the map itself) closes the open card.
    <div className={cn('relative min-h-0 overflow-hidden rounded-xl border border-line-2 bg-surface-2', className)} onKeyDown={onKeyDown} data-testid={testId}>
      <div ref={containerRef} className="absolute inset-0" />
      <div ref={overlayRef} className="pointer-events-none absolute inset-0" data-testid="map-overlay">
        {fadedPositioned.map((item) => (
          <PinMarker key={item.key} place={item.place} x={item.x} y={item.y} faded hovered={hoveredKey === item.key} selected={selectedKey === item.key} onSelect={onSelect} onHover={onHover} />
        ))}
        {clusters.map((cluster) => (cluster.items.length === 1 ? (
          <PinMarker
            key={cluster.key}
            place={cluster.items[0].place}
            x={cluster.x}
            y={cluster.y}
            hovered={hoveredKey === cluster.items[0].key}
            selected={selectedKey === cluster.items[0].key}
            labelled={labelled.has(cluster.key)}
            onSelect={onSelect}
            onHover={onHover}
          />
        ) : (
          <ClusterBubble
            key={cluster.key}
            cluster={cluster}
            hovered={cluster.items.some((item) => item.key === hoveredKey || item.key === selectedKey)}
            labelled={labelled.has(cluster.key)}
            onClick={zoomToCluster}
          />
        )))}
        {card}
      </div>
      {!ready && !error ? (
        <div className="absolute inset-0 grid place-items-center text-[13px] text-ink-3" role="status">
          <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 animate-pulse" aria-hidden="true" />Loading the map…</span>
        </div>
      ) : null}
      {error ? (
        <div className="absolute inset-x-3 top-3 z-50 flex items-start gap-2 rounded-xl border border-line bg-surface p-3 text-[13px] text-ink-2 shadow-raised" role="alert" data-testid="map-load-error">
          <CloudOff className="mt-0.5 h-4 w-4 shrink-0 text-status-red-ink" aria-hidden="true" />
          <span><b className="font-semibold text-ink">{error}</b> The list beside the map still works — ask IT to check the Mapbox key.</span>
        </div>
      ) : null}
    </div>
  );
}

export default MapboxProjectsMap;
